import { useState, useEffect } from 'react'

function useIsMobile(bp = 640) {
  const [v, setV] = useState(() => window.innerWidth < bp)
  useEffect(() => {
    let t; const h = () => { clearTimeout(t); t = setTimeout(() => setV(window.innerWidth < bp), 80) }
    window.addEventListener('resize', h); return () => { clearTimeout(t); window.removeEventListener('resize', h) }
  }, [bp]); return v
}
import { careerRoles, careerVacancies, careerScoreRole, analyzeVacancyByUrl } from '../utils/api'
import ApplicationPackage from './ApplicationPackage'
import PublishedResumesList from './PublishedResumesList'
import CVGenerator from './CVGenerator'
import ApplyQueue from './ApplyQueue'
import ModelToggle from './ModelToggle'

const C = {
  bg:        '#05050d',
  surface:   'rgba(255,255,255,0.028)',
  surface2:  'rgba(255,255,255,0.05)',
  border:    'rgba(180,185,230,0.09)',
  borderHi:  'rgba(180,185,230,0.22)',
  accent:    '#b8bdd4',
  accentDim: 'rgba(180,185,230,0.1)',
  text:      '#dce0f0',
  textMuted: '#8890aa',
  textDim:   '#6a6e85',
  green:     '#4dc88a',
  orange:    '#c8964d',
  red:       '#c85a5a',
}
const font = {
  heading: "'Cormorant Garamond', serif",
  mono:    "'Space Mono', monospace",
  body:    "'Inter', sans-serif",
}

const ROLES_CACHE_KEY = 'career_roles_cache_v3'
const ROLES_TTL_MS = 7 * 24 * 60 * 60 * 1000 // 7 days

function loadCachedRoles(userId) {
  try {
    const raw = localStorage.getItem(ROLES_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (parsed.userId !== userId) return null
    if (Date.now() - parsed.ts > ROLES_TTL_MS) return null
    return parsed.roles
  } catch {
    return null
  }
}

function saveCachedRoles(userId, roles) {
  try {
    localStorage.setItem(ROLES_CACHE_KEY, JSON.stringify({ userId, roles, ts: Date.now() }))
  } catch {}
}

function clearCachedRoles() {
  try { localStorage.removeItem(ROLES_CACHE_KEY) } catch {}
}

function GlassPanel({ children, style = {} }) {
  return (
    <div style={{
      background: C.surface,
      border: `1px solid ${C.border}`,
      borderRadius: 14,
      padding: '20px 24px',
      position: 'relative',
      overflow: 'hidden',
      ...style,
    }}>
      {children}
    </div>
  )
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em',
      textTransform: 'uppercase', color: C.textDim, marginBottom: 14,
    }}>
      {children}
    </div>
  )
}

function FitBadge({ percent, size = 52 }) {
  const color = percent >= 80 ? C.green : percent >= 60 ? C.accent : C.orange
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      border: `2px solid ${color}`, flexShrink: 0,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <span style={{ fontFamily: font.mono, fontSize: size * 0.22, color }}>
        {percent}%
      </span>
    </div>
  )
}

const pill = (color) => ({
  fontFamily: font.body, fontSize: 11, padding: '3px 10px',
  borderRadius: 12, border: `1px solid ${color}50`, color,
  background: color + '12',
})

function PrimaryBtn({ onClick, children, small = false, disabled = false }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: small ? '6px 18px' : '11px 28px',
        borderRadius: 8, cursor: disabled ? 'wait' : 'pointer',
        fontFamily: font.body, fontSize: small ? 12 : 13,
        letterSpacing: '0.04em',
        background: C.accentDim, border: `1px solid ${C.borderHi}`,
        color: disabled ? C.textDim : C.accent,
        transition: 'opacity 0.15s', opacity: disabled ? 0.6 : 1,
      }}
    >
      {children}
    </button>
  )
}

export default function CareerNavigator({ userId = 'default', profileExists = false, rolesState = null, onRolesChange, isOwner = false }) {
  const isMobile = useIsMobile()
  const [careerTab, setCareerTab] = useState('navigator')
  // roles state is lifted to App.jsx to survive tab switches
  const roles = rolesState
  const setRoles = (r) => onRolesChange?.(r)

  const [loadingRoles, setLoadingRoles] = useState(false)
  const [rolesError, setRolesError] = useState(null)

  const [selectedRole, setSelectedRole] = useState(null)
  const [vacancies, setVacancies] = useState(null)      // all scored (accumulated)
  const [displayCount, setDisplayCount] = useState(5)   // how many to show
  const [hhPage, setHhPage] = useState(1)               // next HH.ru page to fetch (0-indexed)
  const [hhTotalPages, setHhTotalPages] = useState(null)
  const [vacFilters, setVacFilters] = useState(null)
  const [loadingMore, setLoadingMore] = useState(false)
  const [loadingVac, setLoadingVac] = useState(false)
  const [vacError, setVacError] = useState(null)
  const [vacCached, setVacCached] = useState(false)
  const [vacCachedAt, setVacCachedAt] = useState(null)

  const [city, setCity] = useState('')
  const [expandedVac, setExpandedVac] = useState(null)
  const [applyVac, setApplyVac]       = useState(null)
  const [showAddRole, setShowAddRole] = useState(false)
  const [customRoleInput, setCustomRoleInput] = useState('')

  // ── By-URL tab state ──
  const [urlInput, setUrlInput]       = useState('')
  const [urlLoading, setUrlLoading]   = useState(false)
  const [urlError, setUrlError]       = useState(null)
  const [urlVacancy, setUrlVacancy]   = useState(null)
  const [urlApplyOpen, setUrlApplyOpen] = useState(false)

  const HH_URL_RE = /hh\.ru\/vacancy\/\d+/

  const analyzeByUrl = async () => {
    if (!HH_URL_RE.test(urlInput)) return
    setUrlLoading(true)
    setUrlError(null)
    setUrlVacancy(null)
    setUrlApplyOpen(false)
    try {
      const res = await analyzeVacancyByUrl(urlInput.trim(), userId)
      if (res.error) { setUrlError(res.error); return }
      setUrlVacancy(res.vacancy)
    } catch (e) {
      setUrlError(e.message)
    } finally {
      setUrlLoading(false)
    }
  }

  // Auto-load roles on mount if no roles yet
  useEffect(() => {
    if (!profileExists || roles) return
    const cached = loadCachedRoles(userId)
    if (cached) {
      setRoles(cached)
      return
    }
    doFetchRoles(false)
  }, [userId, profileExists])

  const doFetchRoles = async (force = true) => {
    if (force) clearCachedRoles()
    setRoles(null)
    setSelectedRole(null)
    setVacancies(null)
    setExpandedVac(null)
    setLoadingRoles(true)
    setRolesError(null)
    try {
      const data = await careerRoles(userId)
      if (data.error) { setRolesError(data.error); return }
      const r = data.roles || []
      setRoles(r)
      saveCachedRoles(userId, r)
    } catch (e) {
      setRolesError(e.message)
    } finally {
      setLoadingRoles(false)
    }
  }

  const [vacHHUrl, setVacHHUrl] = useState(null)

  const roleQueries = (role) => {
    const main = role.hh_query || (role.title || '').split(' ').slice(0, 4).join(' ')
    return { main, extra: role.hh_queries || [] }
  }

  const loadVacancies = async (role, forceRefresh = false) => {
    setVacancies(null)
    setDisplayCount(5)
    setHhPage(1)
    setHhTotalPages(null)
    setExpandedVac(null)
    setVacError(null)
    setVacHHUrl(null)
    setVacCached(false)
    setVacCachedAt(null)
    setVacFilters(null)
    setLoadingVac(true)
    try {
      // Поиск на сервере: город и готовность к переезду берутся из резюме, удалёнка включена, мусор отсеивается
      const { main, extra } = roleQueries(role)
      const data = await careerVacancies(main, userId, city, forceRefresh, { queries: extra })
      setVacFilters(data.filters || null)
      if (data.hh?.length) {
        setVacancies(data.hh)
        setHhTotalPages(data.has_more ? 999 : 1)
        setVacCached(!!data.cached)
        setVacCachedAt(data.cached_at || null)
      } else {
        setVacHHUrl(data.hh_url || `https://hh.ru/search/vacancy?text=${encodeURIComponent(main)}`)
        setVacError(data.error || 'Вакансии не найдены')
      }
    } catch (e) {
      setVacError(e.message)
    } finally {
      setLoadingVac(false)
    }
  }

  const loadMoreVacancies = async () => {
    if (!selectedRole) return

    // Уже оценённые вакансии — просто показываем
    if (displayCount < (vacancies?.length || 0)) {
      setDisplayCount(prev => prev + 5)
      return
    }

    // Следующая страница с сервера
    if (hhPage === null || (hhTotalPages !== null && hhPage >= hhTotalPages)) return

    setLoadingMore(true)
    try {
      const { main, extra } = roleQueries(selectedRole)
      const data = await careerVacancies(main, userId, city, true, { queries: extra, page: hhPage })
      const known = new Set((vacancies || []).map(v => v.id))
      const fresh = (data.hh || []).filter(v => !known.has(v.id))
      setVacancies(prev => [...(prev || []), ...fresh])
      setDisplayCount(prev => prev + 5)
      setHhTotalPages(data.has_more ? 999 : hhPage + 1)
      setHhPage(prev => (prev ?? 0) + 1)
    } catch {
      // silent fail
    } finally {
      setLoadingMore(false)
    }
  }

  const selectRole = (role) => {
    if (selectedRole?.title === role.title) {
      setSelectedRole(null)
      setVacancies(null)
      setDisplayCount(5)
      setHhPage(1)
      setHhTotalPages(null)
      setExpandedVac(null)
      return
    }
    setSelectedRole(role)
    loadVacancies(role, false)
  }

  const addCustomRole = async () => {
    const title = customRoleInput.trim()
    if (!title) return
    setCustomRoleInput('')
    setShowAddRole(false)

    // Add placeholder immediately
    const placeholder = {
      title,
      title_en: title,
      fit_percent: null,
      why_fit: '⏳ Оцениваю соответствие…',
      key_strengths: [],
      missing_skills: [],
      search_query: title,
      hh_query: title.split(' ').slice(0, 3).join(' '),
      custom: true,
      scoring: true,
    }
    const withPlaceholder = [...(roles || []), placeholder]
    setRoles(withPlaceholder)
    saveCachedRoles(userId, withPlaceholder)

    // Score via Claude
    try {
      const scored = await careerScoreRole(title, userId)
      if (!scored.error) {
        scored.custom = true
        setRoles(prev => {
          const updated = (prev || []).map(r => r.title === title ? scored : r)
          saveCachedRoles(userId, updated)
          return updated
        })
      } else {
        // Keep placeholder but remove spinner
        setRoles(prev => {
          const updated = (prev || []).map(r =>
            r.title === title ? { ...r, why_fit: 'Добавлено вручную', scoring: false } : r
          )
          saveCachedRoles(userId, updated)
          return updated
        })
      }
    } catch {
      setRoles(prev => {
        const updated = (prev || []).map(r =>
          r.title === title ? { ...r, why_fit: 'Добавлено вручную', scoring: false } : r
        )
        saveCachedRoles(userId, updated)
        return updated
      })
    }
  }

  const deleteRole = (e, roleTitle) => {
    e.stopPropagation()
    const updated = (roles || []).filter(r => r.title !== roleTitle)
    setRoles(updated)
    saveCachedRoles(userId, updated)
    if (selectedRole?.title === roleTitle) {
      setSelectedRole(null)
      setVacancies(null)
      setExpandedVac(null)
    }
  }

  const formatCachedAt = (iso) => {
    if (!iso) return ''
    try {
      const d = new Date(iso)
      return d.toLocaleString('ru', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
    } catch { return '' }
  }

  // No profile — can't show anything useful
  if (!profileExists) {
    return (
      <div style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div style={{ fontFamily: font.mono, fontSize: 28, color: C.textDim, marginBottom: 20, opacity: 0.4 }}>◈</div>
        <div style={{ fontFamily: font.heading, fontSize: 28, color: C.text, marginBottom: 10, fontWeight: 400 }}>
          Карьерный навигатор
        </div>
        <p style={{ fontFamily: font.body, fontSize: 14, color: C.textMuted, maxWidth: 360, margin: '0 auto', lineHeight: 1.7 }}>
          Сначала загрузите данные и запустите анализ — карьерный навигатор появится автоматически
        </p>
      </div>
    )
  }

  if (loadingRoles) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <div style={{ fontFamily: font.mono, fontSize: 11, color: C.textDim, letterSpacing: '0.1em' }}>
          Claude анализирует профиль и подбирает роли...
        </div>
      </div>
    )
  }

  if (!roles && rolesError) {
    return (
      <div style={{ textAlign: 'center', padding: 60 }}>
        <p style={{ fontFamily: font.body, fontSize: 13, color: C.red, marginBottom: 20 }}>{rolesError}</p>
        <PrimaryBtn onClick={() => doFetchRoles(true)}>Попробовать снова</PrimaryBtn>
      </div>
    )
  }

  if (!roles) return null

  return (
    <div style={{ paddingBottom: 40 }}>

      {/* ── Sub-tabs ── */}
      <div style={{ display: 'flex', gap: 0, borderBottom: `1px solid ${C.border}`, marginBottom: 28 }}>
        {[
          { key: 'navigator', label: 'Навигатор' },
          ...(isOwner ? [{ key: 'queue', label: 'Отклики' }] : []),
          ...(isOwner ? [{ key: 'cv', label: 'CV для HH' }] : []),
          ...(isOwner ? [{ key: 'byurl', label: 'По ссылке' }] : []),
          ...(isOwner ? [{ key: 'published', label: 'Опубликованные резюме' }] : []),
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setCareerTab(t.key)}
            style={{
              fontFamily: font.mono, fontSize: 10, letterSpacing: '0.08em',
              padding: '8px 18px', background: 'transparent', border: 'none',
              cursor: 'pointer', color: careerTab === t.key ? C.text : C.textDim,
              borderBottom: careerTab === t.key ? `2px solid ${C.accent}` : '2px solid transparent',
              transition: 'color 0.15s', marginBottom: -1,
              whiteSpace: 'nowrap',
            }}
          >
            {t.label}
          </button>
        ))}
        <div style={{ marginLeft: 'auto', alignSelf: 'center', paddingBottom: 4, paddingLeft: 12 }}>
          <ModelToggle prefKey="career" label="модель" />
        </div>
      </div>

      {/* ── Apply queue tab ── */}
      {careerTab === 'queue' && isOwner && (
        <ApplyQueue userId={userId} roles={roles} />
      )}

      {/* ── CV Generator tab ── */}
      {careerTab === 'cv' && (
        <CVGenerator userId={userId} />
      )}

      {/* ── Published resumes tab ── */}
      {careerTab === 'published' && (
        <PublishedResumesList userId={userId} />
      )}

      {/* ── By URL tab ── */}
      {careerTab === 'byurl' && (
        <div>
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim, marginBottom: 8 }}>
              Разбор вакансии по ссылке
            </div>
            <div style={{ fontFamily: font.heading, fontSize: 24, color: C.text, fontWeight: 400, marginBottom: 4 }}>
              Вставь ссылку с HH.ru
            </div>
            <div style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted }}>
              Скопируй ссылку вакансии из браузера — Claude оценит соответствие и подготовит пакет отклика
            </div>
          </div>

          {/* URL input row */}
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
            <input
              value={urlInput}
              onChange={e => { setUrlInput(e.target.value); setUrlError(null); setUrlVacancy(null); setUrlApplyOpen(false) }}
              onKeyDown={e => e.key === 'Enter' && analyzeByUrl()}
              placeholder="https://hh.ru/vacancy/12345678"
              style={{
                flex: 1, minWidth: 260,
                padding: '10px 16px', borderRadius: 10,
                fontFamily: font.body, fontSize: 13,
                background: C.surface,
                border: `1px solid ${
                  urlInput && !HH_URL_RE.test(urlInput) ? C.red + '70'
                  : urlInput && HH_URL_RE.test(urlInput) ? C.green + '70'
                  : C.borderHi
                }`,
                color: C.text, outline: 'none',
                transition: 'border-color 0.15s',
              }}
            />
            <PrimaryBtn
              onClick={analyzeByUrl}
              disabled={urlLoading || !HH_URL_RE.test(urlInput)}
            >
              {urlLoading ? '◎ Анализирую...' : 'Разобрать'}
            </PrimaryBtn>
          </div>

          {/* URL format hint */}
          {urlInput && !HH_URL_RE.test(urlInput) && (
            <div style={{ fontFamily: font.body, fontSize: 12, color: C.red, marginBottom: 16 }}>
              Не похоже на ссылку HH.ru. Ожидается: https://hh.ru/vacancy/12345678
            </div>
          )}

          {/* Error */}
          {urlError && (
            <div style={{ fontFamily: font.body, fontSize: 13, color: C.red, padding: '12px 16px', borderRadius: 10, border: `1px solid ${C.red}30`, background: C.red + '0d', marginBottom: 16 }}>
              {urlError}
            </div>
          )}

          {/* Result vacancy card */}
          {urlVacancy && (
            <GlassPanel>
              <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 14 }}>
                {urlVacancy.score != null
                  ? <FitBadge percent={urlVacancy.score} size={52} />
                  : <div style={{ width: 52, height: 52, borderRadius: '50%', border: `2px solid ${C.border}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <span style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim }}>—</span>
                    </div>
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontFamily: font.heading, fontSize: 22, color: C.text, fontWeight: 400, lineHeight: 1.2 }}>
                      {urlVacancy.title}
                    </div>
                    <a
                      href={urlVacancy.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ fontFamily: font.mono, fontSize: 10, color: C.accent, whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      HH.ru ↗
                    </a>
                  </div>
                  <div style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted, marginTop: 4 }}>
                    {urlVacancy.company}
                    {urlVacancy.city ? ` · ${urlVacancy.city}` : ''}
                    {urlVacancy.salary && <span style={{ color: C.accent, marginLeft: 8 }}>{urlVacancy.salary}</span>}
                  </div>
                </div>
              </div>

              {/* Why fits / why not */}
              {urlVacancy.why_fits && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontFamily: font.mono, fontSize: 9, color: C.green, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
                    ✓ Почему подходит
                  </div>
                  <div style={{ fontFamily: font.body, fontSize: 13, color: C.text, lineHeight: 1.6 }}>{urlVacancy.why_fits}</div>
                </div>
              )}
              {urlVacancy.why_not_fits && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: font.mono, fontSize: 9, color: C.orange, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
                    △ Что может не подойти
                  </div>
                  <div style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted, lineHeight: 1.6 }}>{urlVacancy.why_not_fits}</div>
                </div>
              )}

              {/* Apply button */}
              {!urlApplyOpen && (
                <button
                  onClick={() => setUrlApplyOpen(true)}
                  style={{
                    fontFamily: font.body, fontSize: 13, fontWeight: 500,
                    padding: '9px 22px', borderRadius: 9,
                    border: `1px solid ${C.borderHi}`, background: 'rgba(184,189,212,0.07)',
                    color: C.accent, cursor: 'pointer',
                  }}
                >
                  ◎ Откликнуться
                </button>
              )}

              {/* Application Package */}
              {urlApplyOpen && (
                <ApplicationPackage
                  vacancy={urlVacancy}
                  userId={userId}
                  onClose={() => setUrlApplyOpen(false)}
                />
              )}
            </GlassPanel>
          )}
        </div>
      )}

      {careerTab !== 'navigator' && null}
      {careerTab === 'navigator' && <>

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 }}>
        <div>
          <div style={{ fontFamily: font.mono, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: C.textDim, marginBottom: 6 }}>
            Карьерный навигатор
          </div>
          <div style={{ fontFamily: font.heading, fontSize: 26, color: C.text, fontWeight: 400 }}>
            Подходящие роли
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <input
            value={city}
            onChange={e => setCity(e.target.value)}
            placeholder="Город"
            style={{
              padding: '6px 12px', borderRadius: 8, width: 140,
              fontFamily: font.body, fontSize: 12,
              background: C.surface, border: `1px solid ${C.border}`,
              color: C.text, outline: 'none',
            }}
          />
          <PrimaryBtn onClick={() => doFetchRoles(true)} small>Пересчитать</PrimaryBtn>
          <button
            onClick={() => setShowAddRole(v => !v)}
            style={{
              fontFamily: font.mono, fontSize: 10, padding: '6px 12px', borderRadius: 8,
              border: `1px solid ${showAddRole ? C.borderHi : C.border}`,
              background: showAddRole ? 'rgba(184,189,212,0.08)' : 'transparent',
              color: showAddRole ? C.accent : C.textMuted, cursor: 'pointer',
            }}
          >
            + роль
          </button>
        </div>
      </div>

      {/* Add custom role input */}
      {showAddRole && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <input
            value={customRoleInput}
            onChange={e => setCustomRoleInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCustomRole()}
            placeholder="Название роли, например: Data Analyst"
            autoFocus
            style={{
              flex: 1, padding: '8px 14px', borderRadius: 9,
              fontFamily: font.body, fontSize: 13,
              background: C.surface, border: `1px solid ${C.borderHi}`,
              color: C.text, outline: 'none',
            }}
          />
          <button
            onClick={addCustomRole}
            disabled={!customRoleInput.trim()}
            style={{
              fontFamily: font.body, fontSize: 13, fontWeight: 500,
              padding: '8px 18px', borderRadius: 9,
              border: `1px solid ${C.borderHi}`, background: 'rgba(184,189,212,0.1)',
              color: customRoleInput.trim() ? C.accent : C.textDim,
              cursor: customRoleInput.trim() ? 'pointer' : 'default',
            }}
          >
            Добавить
          </button>
        </div>
      )}

      {/* Roles grid */}
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12, marginBottom: 24 }}>
        {roles.map((role, i) => {
          const active = selectedRole?.title === role.title
          return (
            <div
              key={i}
              onClick={() => selectRole(role)}
              style={{
                background: active ? C.accentDim : C.surface,
                border: `1px solid ${active ? C.borderHi : C.border}`,
                borderRadius: 12, padding: 16, cursor: 'pointer',
                transition: 'all 0.15s', position: 'relative',
              }}
            >
              {/* Delete button */}
              <button
                onClick={e => deleteRole(e, role.title)}
                style={{
                  position: 'absolute', top: 10, right: 10,
                  fontFamily: font.mono, fontSize: 10,
                  width: 22, height: 22, borderRadius: '50%',
                  border: `1px solid ${C.border}`, background: 'transparent',
                  color: C.textDim, cursor: 'pointer', lineHeight: 1,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: 0.6,
                }}
                title="Удалить роль"
              >
                ×
              </button>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 10, paddingRight: 28 }}>
                {role.fit_percent != null
                  ? <FitBadge percent={role.fit_percent} />
                  : <div style={{ width: 52, height: 52, borderRadius: '50%', border: `2px solid ${role.scoring ? C.accent + '60' : C.border}`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', animation: role.scoring ? 'pulse 1.4s ease-in-out infinite' : 'none' }}>
                      <span style={{ fontFamily: font.mono, fontSize: 9, color: role.scoring ? C.accent : C.textDim }}>
                        {role.scoring ? '◎' : '—'}
                      </span>
                    </div>
                }
                <div>
                  <div style={{ fontFamily: font.heading, fontSize: 18, color: C.text, lineHeight: 1.2, fontWeight: 400 }}>
                    {role.title}
                  </div>
                  {role.title_en && role.title_en !== role.title && (
                    <div style={{ fontFamily: font.mono, fontSize: 10, color: C.textDim, marginTop: 2, letterSpacing: '0.04em' }}>
                      {role.title_en}
                    </div>
                  )}
                </div>
              </div>
              <p style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, margin: '0 0 10px', lineHeight: 1.6 }}>
                {role.why_fit}
              </p>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {(role.key_strengths || []).map((s, j) => (
                  <span key={j} style={pill(C.green)}>{s}</span>
                ))}
                {(role.missing_skills || []).map((s, j) => (
                  <span key={j} style={pill(C.orange)}>{s} ↑</span>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Vacancies section */}
      {selectedRole && (
        <GlassPanel style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
            <div>
              <SectionLabel>Вакансии · {selectedRole.title}{city ? ` · ${city}` : ''}</SectionLabel>
              {vacFilters && (
                <div style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim, letterSpacing: '0.06em', margin: '-6px 0 10px' }}>
                  {vacFilters.nationwide ? 'вся Россия' : `${vacFilters.city || 'вся Россия'} + удалённая работа`}
                  {vacFilters.dropped && (vacFilters.dropped.junior + vacFilters.dropped.city > 0) ? ` · отсеяно ${vacFilters.dropped.junior + vacFilters.dropped.city} неподходящих` : ''}
                </div>
              )}
              {vacCached && vacCachedAt && (
                <div style={{ fontFamily: font.mono, fontSize: 9, color: C.textDim, marginTop: -8 }}>
                  кэш от {formatCachedAt(vacCachedAt)}
                </div>
              )}
            </div>
            {!loadingVac && (
              <button
                onClick={() => loadVacancies(selectedRole, true)}
                style={{
                  padding: '4px 12px', borderRadius: 6, cursor: 'pointer',
                  fontFamily: font.body, fontSize: 11,
                  background: 'none', border: `1px solid ${C.border}`,
                  color: C.textMuted,
                }}
              >
                ↻ Обновить
              </button>
            )}
          </div>

          {loadingVac && (
            <div style={{ fontFamily: font.mono, fontSize: 11, color: C.textDim, letterSpacing: '0.08em', padding: '20px 0' }}>
              Ищем вакансии и оцениваем соответствие...
            </div>
          )}

          {vacError && !loadingVac && (
            <div>
              <div style={{ fontFamily: font.body, color: C.textMuted, fontSize: 13, marginBottom: vacHHUrl ? 10 : 0 }}>
                {vacError}
              </div>
              {vacHHUrl && (
                <a
                  href={vacHHUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block', padding: '6px 16px', borderRadius: 8,
                    fontFamily: font.body, fontSize: 12,
                    background: C.accentDim, border: `1px solid ${C.borderHi}`,
                    color: C.accent, textDecoration: 'none',
                  }}
                >
                  Открыть поиск на HH.ru ↗
                </a>
              )}
            </div>
          )}

          {vacancies && vacancies.length === 0 && !loadingVac && (
            <div style={{ fontFamily: font.body, color: C.textMuted, fontSize: 13 }}>
              По запросу «{selectedRole.search_query}» вакансии не найдены
            </div>
          )}

          {(vacancies || []).slice(0, displayCount).map((v, i) => {
            const expanded = expandedVac === v.id
            const hasScore = v.score != null
            return (
              <div
                key={i}
                style={{
                  background: expanded ? C.accentDim : 'rgba(255,255,255,0.018)',
                  border: `1px solid ${expanded ? C.borderHi : C.border}`,
                  borderRadius: 10, padding: '14px 16px', marginBottom: 8,
                  transition: 'all 0.15s',
                }}
              >
                <div
                  onClick={() => setExpandedVac(expanded ? null : v.id)}
                  style={{ cursor: 'pointer', display: 'flex', gap: 14, alignItems: 'flex-start' }}
                >
                  {hasScore && <FitBadge percent={v.score} size={44} />}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                      <div style={{ fontFamily: font.body, fontSize: 14, color: C.text, fontWeight: 500, lineHeight: 1.3 }}>
                        {v.title}
                      </div>
                      <a
                        href={v.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        style={{ fontFamily: font.mono, fontSize: 10, color: C.accent, whiteSpace: 'nowrap', flexShrink: 0 }}
                      >
                        HH.ru ↗
                      </a>
                    </div>
                    <div style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                      {v.company}{v.city ? ` · ${v.city}` : ''}
                      {v.salary && <span style={{ color: C.accent, marginLeft: 8 }}>{v.salary}</span>}
                    </div>
                  </div>
                </div>

                {expanded && (
                  <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.border}` }}>
                    {v.requirement && (
                      <p style={{ fontFamily: font.body, fontSize: 12, color: C.textMuted, margin: '0 0 14px', lineHeight: 1.6 }}>
                        {v.requirement}
                      </p>
                    )}
                    {v.why_fits && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontFamily: font.mono, fontSize: 9, color: C.green, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
                          ✓ Почему подходит
                        </div>
                        <div style={{ fontFamily: font.body, fontSize: 13, color: C.text, lineHeight: 1.6 }}>{v.why_fits}</div>
                      </div>
                    )}
                    {v.why_not_fits && (
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontFamily: font.mono, fontSize: 9, color: C.orange, letterSpacing: '0.12em', textTransform: 'uppercase', marginBottom: 4 }}>
                          △ Что может не подойти
                        </div>
                        <div style={{ fontFamily: font.body, fontSize: 13, color: C.textMuted, lineHeight: 1.6 }}>{v.why_not_fits}</div>
                      </div>
                    )}

                    {/* Откликнуться button */}
                    {applyVac !== v.id && (
                      <button
                        onClick={e => { e.stopPropagation(); setApplyVac(v.id) }}
                        style={{
                          marginTop: 10, fontFamily: font.body, fontSize: 12, fontWeight: 500,
                          padding: '8px 20px', borderRadius: 9,
                          border: `1px solid ${C.borderHi}`, background: 'rgba(184,189,212,0.07)',
                          color: C.accent, cursor: 'pointer',
                        }}
                      >
                        ◎ Откликнуться
                      </button>
                    )}

                    {/* Application Package */}
                    {applyVac === v.id && (
                      <ApplicationPackage
                        vacancy={v}
                        userId={userId}
                        onClose={() => setApplyVac(null)}
                      />
                    )}
                  </div>
                )}
              </div>
            )
          })}
          {/* Load more button */}
          {(() => {
            const canLoadMore = vacancies && (
              displayCount < vacancies.length ||
              (hhPage !== null && (hhTotalPages === null || hhPage < hhTotalPages))
            )
            if (!canLoadMore || loadingVac) return null
            return (
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <button
                  onClick={loadMoreVacancies}
                  disabled={loadingMore}
                  style={{
                    fontFamily: font.body, fontSize: 13,
                    padding: '9px 28px', borderRadius: 9,
                    border: `1px solid ${C.borderHi}`,
                    background: loadingMore ? 'rgba(184,189,212,0.05)' : 'rgba(184,189,212,0.07)',
                    color: loadingMore ? C.textDim : C.accent,
                    cursor: loadingMore ? 'default' : 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {loadingMore ? '⏳ загружаю...' : '+ ещё 5 вакансий'}
                </button>
                {vacancies && (
                  <div style={{ marginTop: 8, fontFamily: font.mono, fontSize: 9, color: C.textDim }}>
                    показано {Math.min(displayCount, vacancies.length)} из {vacancies.length} проскоренных
                  </div>
                )}
              </div>
            )
          })()}
        </GlassPanel>
      )}
      </>}
    </div>
  )
}
